import { createRouter, createWebHistory } from 'vue-router'
import pinia from "@/stores/create-store"
import axios from 'axios'
import { useAuthStore } from '@/stores/user/auth'
import { useViewLoadingStore } from '@/stores/loading/view-loading'
import { useToastsStore } from '@/stores/notifications/toasts'

const storeAuth = useAuthStore(pinia)
const storeViewLoading = useViewLoadingStore(pinia)
const storeToasts = useToastsStore(pinia)

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Public routes
    {
        path: '/',
        name: 'login',
        component: () => import('../views/Public/LoginView.vue'),
        meta: { layout: 'PublicLayout', requiresAuth: false }
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('../views/Public/RegisterView.vue'),
        meta: { layout: 'PublicLayout', requiresAuth: false }
    },
    {
        path: '/verify',
        name: 'verify',
        component: () => import('../views/Public/VerifyEmail.vue'),
        meta: { layout: 'PublicLayout', requiresAuth: false }
    },
    {
        path: '/password',
        name: 'password',
        component: () => import('../views/Public/PasswordResetView.vue'),
        meta: { layout: 'PublicLayout', requiresAuth: false }
    },
    {
        path: '/reset-password',
        name: 'reset.password',
        component: () => import('../views/Public/PasswordResetFormView.vue'),
        meta: { layout: 'PublicLayout', requiresAuth: false }
    },
    // Authenticated routes
    {
        path: '/dashboard',
        name: 'home',
        component: () => import('../views/DashboardView.vue'),
        meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
        path: '/explore',
        name: 'explore',
        component: () => import('../views/ExploreView.vue'),
        meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
        path: '/bookmarks',
        name: 'bookmarks',
        component: () => import('../views/BookmarksView.vue'),
        meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
        path: '/tags',
        name: 'tags',
        component: () => import('../views/TagsView.vue'),
        meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
        path: '/followers',
        name: 'followers',
        component: () => import('../views/FollowersView.vue'),
        meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
        path: '/collection/:collection_id',
        name: 'collection',
        component: () => import('../views/Collection/CollectionView.vue'),
        meta: { layout: 'MainLayout', requiresAuth: true },
        children: [
          {
            path: 'ginnel/:ginnel_id',
            name: 'collection.ginnel',
            component: () => import('../views/Ginnel/GinnelView.vue'),
            meta: { layout: 'MainLayout', requiresAuth: true },
          }
        ],
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('../views/Account/AccountView.vue'),
      meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/Account/ProfileView.vue'),
      meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('../views/Error404View.vue'), meta: { layout: 'MainLayout', requiresAuth: true }
    },
    {
      path: '/maintenance.html',
      redirect: { name: 'home' }
    },
  ],

  scrollBehavior: async (to, from, savedPosition) => {
    if (savedPosition) {
      return {
        top: savedPosition.top,
        behavior: 'instant'
      }
    } else {
      return {
        top: 0,
        behavior: 'smooth'
      }
    }
  },
})

router.beforeEach(async (to, from, next) => {
  // Check if authentication has been checked
  if (! storeAuth.checkedAuthentication || to.meta.layout !== 'PublicLayout') {
    try {
      // Wait for authentication check to complete
      await storeAuth.checkAuthentication()

      // Handle route navigation based on authentication status
      if (to.meta.layout !== 'PublicLayout') {
        if (storeAuth.isAuthenticated) {
          next() // Allow access to authenticated routes
        } else {
          next({ name: 'login', query: { redirect: to.fullPath } }) // Redirect to login
        }
      } else {
        // For public routes
        if (storeAuth.isAuthenticated) {
          next({ name: 'home' }) // Redirect away from public pages if authenticated
        } else {
          next() // Allow access to public pages
        }
      }
    } catch (error) {
      if (to.meta.layout === 'PublicLayout') {
        next()
      } else {
        next({ name: 'login', query: { redirect: to.fullPath } }) // Redirect to login on error
      }
    }
  } else {
    // If authentication has already been checked, proceed with navigation
    next()
  }
})

router.afterEach((to, from) => {
  // Turn off the loading indicator after navigation completes
  storeViewLoading.updateLoading(false)
})

export default router
